import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "toggleOnClick"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useState } from 'react';
import { useSafeLayoutEffect, useEvent, useBooleanEvent, useForkRef } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useButton } from '../button/button.js';
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element that controls the visibility of a
 * disclosure content element.
 * @see https://ariakit.org/components/disclosure
 * @example
 * ```jsx
 * const state = useDisclosureState();
 * const props = useDisclosure({ state });
 * <Role {...props}>Disclosure</Role>
 * <DisclosureContent state={state}>Content</DisclosureContent>
 * ```
 */

var useDisclosure = createHook(function (_ref) {
  var _state$contentElement;

  var state = _ref.state,
      _ref$toggleOnClick = _ref.toggleOnClick,
      toggleOnClick = _ref$toggleOnClick === void 0 ? true : _ref$toggleOnClick,
      props = _objectWithoutProperties(_ref, _excluded);

  var ref = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      expanded = _useState2[0],
      setExpanded = _useState2[1]; // Assigns the disclosureRef whenever it's undefined or disconnected from
  // the DOM. If this disclosure element is the disclosureRef, this element
  // will get the `aria-expanded` attribute set to `true` when the disclosure
  // content is open.


  useSafeLayoutEffect(function () {
    var currentDisclosure = state.disclosureRef.current;

    if (!currentDisclosure || !currentDisclosure.isConnected) {
      state.disclosureRef.current = ref.current;
    }

    var isCurrentDisclosure = state.disclosureRef.current === ref.current;
    setExpanded(state.open && isCurrentDisclosure);
  }, [state.disclosureRef, state.open]);
  var onMouseDownProp = props.onMouseDown;
  var onMouseDown = useEvent(function (event) {
    state.disclosureRef.current = event.currentTarget;
    onMouseDownProp == null ? void 0 : onMouseDownProp(event);
  });
  var onClickProp = props.onClick;
  var toggleOnClickProp = useBooleanEvent(toggleOnClick);
  var isDuplicate = ("data-disclosure" in props);
  var onClick = useEvent(function (event) {
    state.disclosureRef.current = event.currentTarget;
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (isDuplicate) return;
    if (!toggleOnClickProp(event)) return;
    state.toggle();
  });
  props = _objectSpread(_objectSpread({
    "data-disclosure": "",
    "aria-expanded": expanded,
    "aria-controls": (_state$contentElement = state.contentElement) == null ? void 0 : _state$contentElement.id
  }, props), {}, {
    ref: useForkRef(ref, props.ref),
    onMouseDown: onMouseDown,
    onClick: onClick
  });
  props = useButton(props);
  return props;
});
/**
 * A component that renders an element that controls the visibility of a
 * disclosure content element.
 * @see https://ariakit.org/components/disclosure
 * @example
 * ```jsx
 * const disclosure = useDisclosureState();
 * <Disclosure state={disclosure}>Disclosure</Disclosure>
 * <DisclosureContent state={disclosure}>Content</DisclosureContent>
 * ```
 */

var Disclosure = createComponent(function (props) {
  var htmlProps = useDisclosure(props);
  return createElement("button", htmlProps);
});
export { Disclosure, useDisclosure };